<template>
  <div id="screenings-index">
      <Breadcrumbs
        :breadcrumbs="[
          { title: this.$t('menu.agenda'), disabled: true },
          { title: this.$t('agenda.screenings.page_title'), disabled: true }
        ]">

        <div slot="breadcrumbAfter" class="d-flex align-items-center">
          <va-popover
            placement="left"
            :message="$t('agenda.screenings.download.button')">
            <span class="i-cloud-download" @click.prevent="downloadReports()" :disabled="isLoading || isLoadingExcel"></span>
          </va-popover>
        </div>

      </Breadcrumbs>

      <div class="btn btn__floating btn__round__large" v-if="writableAvailabilitiesEntities.length > 0" @click="showAvailabilityForm = true">
        <va-popover
          placement="left"
          :message="$t('agenda.screenings.create_availability')"
        >
          <va-button
            @click.prevent="isCreatingAvailability = true">
            <i class="fa fa-plus"></i>
          </va-button>
        </va-popover>
      </div>

      <section id="filters">
        <filters
          @filters-updated="onFiltersUpdated"
          @loaded="onFiltersLoaded"
        >
        </filters>
      </section>

      <section id="screenings-wrapper">

      <div id="screenings-header" class="mb-3">
        <h5 class="mb-0 mb-sm-2">
          {{$t('agenda.screenings.view_by_option', {option: $t(selectedViewOption.name)})}}
        </h5>
        <div class="d-flex flex-column flex-sm-row justify-content-start justify-content-sm-between align-items-center">
          <span>
            {{$t('agenda.screenings.number_of_results', {number: filteredData.length})}}
          </span>
          <div class="d-flex align-items-center flex-wrap justify-content-start mt-3 mt-md-0">

              <div class="d-flex align-items-center">
                <label class="mr-2 is-bold">
                  {{$t('agenda.screenings.view_options.label')}}:
                </label>

                <va-popover v-for="option in Object.values(viewOptions)" :key="option.id" :message="$t(option.name)" placement="top" class="mr-3">
                  <span class="view-option-icon" :class="[option.icon, option.id == selectedViewOption.id ? 'active' : '']" @click="selectedViewOption = option"></span>
                </va-popover>
              </div>

              <div class="d-flex align-items-center ml-0 ml-md-5">
                <label class="is-bold">{{$t('agenda.screenings.show_map')}}:</label>
                <va-toggle
                  class="mb-1"
                  v-model="showMap"
                  :small="true"
                />
              </div>
            </div>
          </div>
        </div>

        <div v-if="isLoading || !hasLoadedLocalStorage" class="d-flex align-items-center flex-column py-5">
          <span class="mb-3">{{$t('agenda.screenings.loading')}}</span>
          <hollow-dots-spinner
            :animation-duration="1000"
            :size="60"
            color="#29c4a9"
          />
        </div>

        <div
          v-else-if="filteredData.length > 0"
          id="screenings-list-wrapper"
          class="d-flex flex-column flex-sm-row">

          <ScreeningsList
            id="screenings-list"
            :class="{'d-none d-sm-block col-sm-6 col-md-8 overflow-y-auto': showMap, 'col-12': !showMap}"
            :data="filteredData"
            :groupFunction="selectedViewOption.groupFunction"
            :titleFunction="selectedViewOption.titleFunction"
            :stickyTitle="showMap"
            :facilities="facilitiesAdmin"
            @onRowClicked="onScreeningListItemClicked"
            @sendQRCode="onSendQRCode"
            @openLeadsListing="onOpenAnonLeadsList"
            @openAppointments="onOpenAppointmentsList"
            @updateScreening="onUpdateScreening"
            @showSessionReport="openSessionReportModal"
            @generateSessionReport="openSessionReportModal"
          >
          </ScreeningsList>

          <ScreeningsMap
            v-show="showMap"
            id="screenings-map"
            ref="screeningsMap"
            class="col-12 col-sm-6 col-md-4 px-0"
            :data="filteredData"
            @updateScreening="onUpdateScreening"
          >
          </ScreeningsMap>

        </div>

        <div v-else class="text-center">
          <span>
            {{$t('agenda.screenings.no_data_available')}}
          </span>
        </div>
      </section>

    <ScreeningReportModal
      v-if="showSessionReport"
      :screening="selectedScreening"
      @report-created="onReportCreated"
      @report-updated="onReportUpdated"
      @report-deleted="onReportDeleted"
      @modal-closed="onCloseScreeningReport"
    />

    <AvailabilityForm
        v-if="showAvailabilityForm"
        :availableActivities="activities"
        :facilities="facilitiesAdmin"
        :selectedAvailability="selectedScreening"
        @modal-closed="onAvailabilityFormClosed"
        @availability-created="onAvailabilityCreated"
        @availability-updated="onAvailabilityUpdated"
        @availability-deleted="onAvailabilityDestroyed"
    />

    <AnonLeadsListModal
      v-if="showAnonLeadsList"
      :sessionUrl="selectedScreening.session_uri"
      @modalClosed="onCloseAnonLeadsList"
    />

    <AppointmentsListModal
      v-if="showAppointmentsList"
      :screening="selectedScreening"
      @modalClosed="onCloseAppointmentsList"
    />

    <SendQRCodeModal
      v-if="showSendQrCode"
      :availability="selectedScreening"
      @modalClosed="onCloseQrCodeModal"
    />

  </div>
</template>

<script>
import axios from 'axios'
import { debounce } from 'lodash'
import { HollowDotsSpinner } from 'epic-spinners'
import * as utils from './utils/screenings.js'
import { FacilityService, AvailabilityService } from '@/api/BookingService'
import { ActivityService } from '@/api/AuthService'
import Filters from './Filters'
import ScreeningsList from './ScreeningsList'
import ScreeningsMap from './ScreeningsMap'
import ScreeningReportModal from './ScreeningReportModal'
import AvailabilityForm from '@/components/agenda/availabilities/AvailabilityForm'
import SendQRCodeModal from '@/components/agenda/availabilities/SendQRCodeModal'
import AnonLeadsListModal from './AnonLeadsListModal'
import AppointmentsListModal from './AppointmentsListModal'
import Breadcrumbs from '@/components/common/Breadcrumbs'

const availabilityInfoType = 200
const appointmentInfoType = 400

export default {
  components: {
    Breadcrumbs,
    Filters,
    ScreeningReportModal,
    ScreeningsList,
    ScreeningsMap,
    AvailabilityForm,
    AnonLeadsListModal,
    AppointmentsListModal,
    SendQRCodeModal,
    HollowDotsSpinner
  },
  computed: {
    ebookingService: function () {
      return this.$store.state.services.find(service => service.url === this.$store.state.ebooking_server)
    },
    availabilityPermissions: function () {
      return this.ebookingService.user_permissions.filter(permission => permission.info_type === availabilityInfoType)
    },
    appointmentPermissions: function () {
      return this.ebookingService.user_permissions.filter(permission => permission.info_type === appointmentInfoType)
    },
    writableAvailabilitiesEntities: function () {
      let userPermissions = this.availabilityPermissions.filter(permission => permission.access_type >= 2)

      if (userPermissions.length === 0) {
        return []
      } else {
        if (userPermissions.some(permission => permission.entity_id === null)) {
          return [...this.ebookingService.entities]
        } else {
          return this.ebookingService.entities.filter(entity => userPermissions.some(permission => permission.entity_id === entity.id))
        }
      }
    },
    filteredData: function () {
      let filteredData = []
      if (this.hasLoadedLocalStorage) {
        filteredData = this.availabilities

        if (this.searchQuery.trim()) {
          let query = this.searchQuery.toLowerCase()

          filteredData = filteredData.filter(availability => {
            return (availability.facility && availability.facility.name.toLowerCase().includes(query)) ||
              (availability.kiosk && availability.kiosk.serial && availability.kiosk.serial.toLowerCase().includes(query)) ||
              (availability.facility && availability.facility.address && availability.facility.address.city && availability.facility.address.city.toLowerCase().includes(query)) ||
              (availability.facility && availability.facility.address && availability.facility.address.line1 && availability.facility.address.line1.toLowerCase().includes(query)) ||
              (availability.user && availability.user.name.toLowerCase().includes(query)) ||
              (availability.user === null && this.$t('agenda.screenings.labels.undefined_provider').toLowerCase().includes(query))
          })
        }
      }
      return filteredData
    }
  },
  data: function () {
    return {
      hasLoadedLocalStorage: false,
      isLoading: false,
      isLoadingExcel: false,
      searchQuery: '',
      requestParams: {
        startDate: '',
        endDate: '',
        entities: '',
        audiologyProviders: '',
        providers: '',
        activities: '3',
        with: 'kiosks'
      },
      viewOptions: [
        {
          id: 1,
          name: 'agenda.screenings.view_options.view_by_facility',
          icon: 'i-facility',
          groupFunction: utils.groupByFacility,
          titleFunction: this.viewByFacilityTitle
        },
        {
          id: 2,
          name: 'agenda.screenings.view_options.view_by_month',
          icon: 'i-calendar',
          groupFunction: utils.groupByMonth,
          titleFunction: this.viewByMonthTitle
        },
        {
          id: 3,
          name: 'agenda.screenings.view_options.view_by_promotor',
          icon: 'i-user',
          groupFunction: utils.groupByPromotor,
          titleFunction: this.viewByPromotorTitle
        }
      ],
      availabilities: [],
      cancelationCallback: null,
      showAvailabilityForm: false,
      showSessionReport: false,
      showAnonLeadsList: false,
      showAppointmentsList: false,
      showSendQrCode: false,
      showMap: false,
      selectedViewOption: null,
      selectedScreening: null,
      activities: [],
      facilitiesAdmin: []
    }
  },
  created: function () {
    this.selectedViewOption = this.viewOptions[0]
    this.loadActivities()
    this.loadFacilitiesAdmin()
  },
  methods: {
    loadActivities: function () {
      ActivityService.query()
        .then(activities => {
          this.activities = activities
        })
        .catch(errors => console.log(errors))
    },
    loadFacilitiesAdmin: function () {
      FacilityService.access({ access: 3, info: 200 })
        .then(facilities => {
          this.facilitiesAdmin = facilities
        }).catch(errors => {
          console.log('errors', errors)
        })
    },
    getAvailabilities: function () {
      if (this.hasLoadedLocalStorage && !this.isLoading) {
        this.isLoading = true

        if (this.cancelationCallback !== null) {
          this.cancelationCallback()
        }

        AvailabilityService.cancelableQuery(this.requestParams, new axios.CancelToken((c) => {
          this.cancelationCallback = c
        }))
          .then(availabilities => {
            this.availabilities = availabilities.map(availability => {
              return {
                ...availability,
                start: this.$options.filters.localTime(availability.start, 'YYYY-MM-DD HH:mm:ss'),
                end: this.$options.filters.localTime(availability.end, 'YYYY-MM-DD HH:mm:ss')
              }
            })
          })
          .catch(err => {
            console.log(err)
            this.showToast(this.$t('agenda.screenings.something_went_wrong'), this.$store.state.errorToastOptions)
          })
          .finally(() => {
            this.cancelationCallback = null
            this.isLoading = false
          })
      }
    },
    viewByFacilityTitle: function (group) {
      if (group.facility) {
        let title = group.facility.name
        if (group.facility.address) {
          title = `${title} (${group.facility.address.line1} - ${group.facility.address.city})`
        }
        return title
      } else {
        return this.$t('agenda.screenings.no_facility')
      }
    },
    viewByMonthTitle: function (group) {
      return `${group.date.format('MMMM')} ${group.date.format('YYYY')}`
    },
    viewByPromotorTitle: function (group) {
      return group.promotor ? group.promotor.name : this.$t('agenda.screenings.no_promotor')
    },
    openSessionReportModal: function (screening) {
      this.showSessionReport = true
      this.selectedScreening = screening
    },
    onCloseScreeningReport: function () {
      this.showSessionReport = false
      this.selectedScreening = null
    },
    onReportCreated: function ({ report, screening }) {
      this.selectedScreening.report_id = report.id
    },
    onReportUpdated: function ({ report, screening }) {
      this.selectedScreening.report_id = report.id
    },
    onReportDeleted: function (screening) {
      this.selectedScreening.report_id = null
    },
    onUpdateScreening: function (screening) {
      this.selectedScreening = { ...screening, public: !screening.private }
      this.showAvailabilityForm = true
    },
    onAvailabilityCreated: function () {
      this.showAvailabilityForm = false
      this.getAvailabilities()
    },
    onAvailabilityUpdated: function () {
      this.showAvailabilityForm = false
      this.selectedScreening = null
      this.getAvailabilities()
    },
    onAvailabilityDestroyed: function () {
      this.showAvailabilityForm = false
      this.selectedScreening = null
      this.getAvailabilities()
    },
    onAvailabilityFormClosed: function () {
      this.showAvailabilityForm = false
      this.selectedScreening = null
    },
    onScreeningListItemClicked: function (screening) {
      if (this.showMap) {
        this.$refs.screeningsMap.selectScreening(screening)
      }
    },
    onSendQRCode: function (screening) {
      this.showSendQrCode = true
      this.selectedScreening = screening
    },
    onCloseQrCodeModal: function () {
      this.showSendQrCode = false
      this.selectedScreening = null
    },
    onOpenAnonLeadsList: function (screening) {
      this.showAnonLeadsList = true
      this.selectedScreening = screening
    },
    onCloseAnonLeadsList: function () {
      this.showAnonLeadsList = false
      this.selectedScreening = null
    },
    onOpenAppointmentsList: function (screening) {
      this.showAppointmentsList = true
      this.selectedScreening = screening
    },
    onCloseAppointmentsList: function () {
      this.showAppointmentsList = false
      this.selectedScreening = null
    },
    downloadReports: function () {
      if (this.isLoading || this.isLoadingExcel) return
      this.isLoadingExcel = true
      let url = this.$store.state.ebooking_server + '/screenings-reports-excel'
      this.$http({
        url: url,
        method: 'GET',
        params: this.requestParams,
        responseType: 'blob',
      }).then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'reports.xlsx')
        document.body.appendChild(link)
        link.click()
      }).catch((error) => {
        if (error.response.status === 404) {
          this.showToast(this.$t('agenda.screenings.download.noreports'), this.$store.state.errorToastOptions)
        } else {
          this.showToast(this.$t('agenda.screenings.download.error'), this.$store.state.errorToastOptions)
        }
        console.log(error)
      }).finally(() => {
        this.isLoadingExcel = false
      })
    },
    onFiltersUpdated: function ({ filters }) {
      this.searchQuery = filters.query
      this.requestParams.startDate = filters.dateRange.start
      this.requestParams.endDate = filters.dateRange.end
      this.requestParams.entities = filters.selectedEntityIds.join(',')
      this.requestParams.audiologyProviders = filters.audProviders.map(e => e.id).join(',')
      this.requestParams.providers = filters.screeningProviders.map(e => e.id).join(',')
    },
    onFiltersLoaded: function () {
      this.hasLoadedLocalStorage = true
      this.getAvailabilities()
    }
  },
  watch: {
    'requestParams': {
      handler: debounce(function (params) {
        this.getAvailabilities()
      }, 750),
      deep: true
    }
  }
}
</script>

<style lang="scss">

#screenings-index {
  .view-option-icon {
    display: block;
    width: 2rem;
    height: 2rem;
    text-align: center;
  }

  .view-option-icon {
    cursor: pointer;
  }

  .overflow-y-auto {
    overflow-y: auto;
    height: 75vh;
  }
}

</style>
